import { IonButton, useIonAlert } from '@ionic/react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Capacitor } from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';

import { ProcedurePills, Spacer } from '@/components/common';
import { Icon, Text } from '@/components/memo';
import {
  useClinic,
  useDentist,
  useRequests,
  useSubscription
} from '@/contexts';
import { Request } from '@/data/Requests';
import {
  ACCESS_TYPES,
  REQUEST_ACTIONS,
  REQUEST_ACTION_ICONS,
  REQUEST_RESCHEDULE_METHODS,
  REQUEST_STATUS_OPTIONS
} from '@/utils/constants';
import { canEditRequestProcedures } from '@/utils/helpers/appointmentPurpose';
import capitalize from '@/utils/helpers/capitalize';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { formatName } from '@/utils/helpers/formatName';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import { PAYWALL_SOURCES } from '@/utils/tracker';
import { RequestAction } from '@/utils/types';
import { format, isBefore } from 'date-fns';
import {
  calendarClearOutline,
  callOutline,
  cardOutline,
  createOutline,
  timeOutline
} from 'ionicons/icons';

import './RequestItem.css';

interface RequestProps {
  request: Request;
  setRequestToReschedule: React.Dispatch<React.SetStateAction<Request | null>>;
  onOpenPaywall?: (options?: { source?: string; action?: string }) => void;
  onEditProcedures?: (request: Request) => void;
}

const RequestItem: React.FC<RequestProps> = ({
  request,
  setRequestToReschedule,
  onOpenPaywall,
  onEditProcedures
}) => {
  const requestItemRef = useRef<HTMLDivElement>(null);
  const isNativePlatform = Capacitor.isNativePlatform();

  const { patient, status, date, start_time, end_time, purpose, hmo_provider } =
    request;
  const { selectedDentist: dentist } = useDentist();
  const { isClinic } = useClinic();
  const { isSubscribed } = useSubscription();
  const {
    focusedRequestId,
    approveRequestMutation,
    declineRequestMutation,
    cancelRequestMutation
  } = useRequests();

  const [presentAlert] = useIonAlert();

  const isDemoBooking = useMemo(() => {
    return (
      patient?.mobile_number === null ||
      patient?.mobile_number === dentist?.mobile_number
    );
  }, [patient?.mobile_number, dentist?.mobile_number]);

  const handleReschedule = useCallback(async () => {
    if (isSubscribed === false && onOpenPaywall && !isDemoBooking) {
      onOpenPaywall({
        source: PAYWALL_SOURCES.REQUESTS,
        action: 'reschedule'
      });
      return;
    }

    await presentAlert({
      header: 'Reschedule',
      message: `How would you like to reschedule your appointment with ${formatName(patient?.first_name)}?`,
      inputs: [
        {
          type: 'radio',
          label: 'Manually enter a new date and time',
          value: REQUEST_RESCHEDULE_METHODS.MANUAL,
          checked: true
        },
        {
          type: 'radio',
          label:
            'Cancel appointment and send a new booking link to your patient',
          value: REQUEST_RESCHEDULE_METHODS.AUTOMATIC
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Confirm',
          handler: async (selectedMethod: string) => {
            if (selectedMethod === REQUEST_RESCHEDULE_METHODS.MANUAL) {
              setRequestToReschedule(request);
            } else if (
              selectedMethod === REQUEST_RESCHEDULE_METHODS.AUTOMATIC
            ) {
              await handleAction(REQUEST_ACTIONS.CANCEL);
            }
          }
        }
      ]
    });
  }, [
    request.id,
    presentAlert,
    patient?.first_name,
    isSubscribed,
    onOpenPaywall,
    isDemoBooking
  ]);

  const handleAction = useCallback(
    async (action: RequestAction) => {
      if (isSubscribed === false && onOpenPaywall && !isDemoBooking) {
        onOpenPaywall({
          source: PAYWALL_SOURCES.REQUESTS,
          action: action
        });
        return;
      }

      if (action === REQUEST_ACTIONS.RESCHEDULE) {
        await handleReschedule();
        return;
      }

      const { value: confirmed } = await Dialog.confirm({
        title: 'Confirm',
        message: `Are you sure you want to ${action.toUpperCase()} ${
          action === REQUEST_ACTIONS.CANCEL
            ? 'your appointment with'
            : 'the request from'
        } ${formatName(patient?.first_name)}?`,
        okButtonTitle: 'Yes'
      });

      if (!confirmed) return;

      if (action === REQUEST_ACTIONS.APPROVE) {
        await approveRequestMutation.mutateAsync({ request });
      } else if (
        action === REQUEST_ACTIONS.DECLINE ||
        action === REQUEST_ACTIONS.CANCEL
      ) {
        const { value: reason, cancelled } = await Dialog.prompt({
          title: `Reason for ${action}`,
          message: 'Please provide a reason or any additional notes',
          inputPlaceholder: 'Enter reason or notes (optional)'
        });
        if (cancelled) return;

        if (action === REQUEST_ACTIONS.DECLINE) {
          await declineRequestMutation.mutateAsync({
            request,
            reason: reason === '' ? null : reason
          });
        } else {
          await cancelRequestMutation.mutateAsync({
            request,
            reason: reason === '' ? null : reason
          });
        }
      }
    },
    [
      request,
      patient?.first_name,
      handleReschedule,
      approveRequestMutation,
      declineRequestMutation,
      cancelRequestMutation,
      isSubscribed,
      onOpenPaywall,
      isDemoBooking
    ]
  );

  const renderActionButton = (
    action: RequestAction,
    color: string,
    fill: 'clear' | 'solid' = 'solid'
  ) => (
    <IonButton
      expand="block"
      onClick={() => handleAction(action)}
      color={color}
      fill={fill}
      strong={true}
    >
      <Icon slot="start" icon={REQUEST_ACTION_ICONS[action]} />
      {capitalize(action)}
    </IonButton>
  );

  const hasAccess = useMemo(() => {
    return isClinic || dentist?.access === ACCESS_TYPES.EDIT;
  }, [isClinic, dentist?.access]);

  useEffect(() => {
    if (focusedRequestId === request.id) {
      requestItemRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [focusedRequestId, request.id, requestItemRef.current]);

  const showEditIcon =
    hasAccess && canEditRequestProcedures(request) && onEditProcedures;

  return (
    <div className="request-item" ref={requestItemRef}>
      <div className="request-item__details">
        <div className="request-item__content-header">
          <div className="request-item__patient">
            <p className="request-item__patient-name">
              {formatName(patient?.first_name)}{' '}
              <Text color="primary">{formatName(patient?.last_name)}</Text>
            </p>
            {hmo_provider && <p className="request-item__insurance-tag">HMO</p>}
          </div>
          {showEditIcon && (
            <button
              type="button"
              className="request-item__edit-procedures"
              aria-label="Edit Procedures"
              onClick={() => onEditProcedures(request)}
            >
              <Icon icon={createOutline} />
            </button>
          )}
        </div>
        <Spacer top={8} bottom={8}>
          <ProcedurePills procedures={purpose?.split(',') || []} />
        </Spacer>
        <div className="request-item__schedule">
          <div>
            <Icon icon={calendarClearOutline} />
            <p>{format(parseDate(date), 'MMMM d, yyyy')}</p>
          </div>
          <div>
            <Icon icon={timeOutline} />
            <p>
              {format(parseTime(start_time), 'h:mm a')} -{' '}
              {format(parseTime(end_time), 'h:mm a')}
            </p>
          </div>
          {patient?.mobile_number && (
            <div>
              <Icon icon={callOutline} />
              <p>{formatMobileNumber(patient.mobile_number)}</p>
            </div>
          )}
          {hmo_provider && (
            <div>
              <Icon icon={cardOutline} />
              <p>{hmo_provider}</p>
            </div>
          )}
        </div>
      </div>
      {hasAccess &&
        isNativePlatform &&
        status === REQUEST_STATUS_OPTIONS.PENDING && (
          <Spacer bottom={-8}>
            <div className="request-item__actions">
              {renderActionButton(REQUEST_ACTIONS.APPROVE, 'success')}
              {renderActionButton(REQUEST_ACTIONS.DECLINE, 'danger', 'clear')}
            </div>
          </Spacer>
        )}
      {hasAccess &&
        isNativePlatform &&
        (status === REQUEST_STATUS_OPTIONS.APPROVED ||
          status === REQUEST_STATUS_OPTIONS.RESCHEDULED) &&
        date &&
        start_time &&
        isBefore(now(), parseDate(`${date}T${start_time}`)) && (
          <Spacer bottom={-8}>
            <div className="request-item__actions">
              {renderActionButton(REQUEST_ACTIONS.RESCHEDULE, 'dark')}
              {renderActionButton(REQUEST_ACTIONS.CANCEL, 'danger', 'clear')}
            </div>
          </Spacer>
        )}
    </div>
  );
};

export default RequestItem;
